import { PlusCircle, ArrowLeft } from "feather-icons-react/build/IconComponents";
import { useContext, useEffect, useState } from "react";
import { ArrowRepeat, CashCoin, People, Wallet2 } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import API from "../../API/newIndex";
import { DataLoad } from "../../App";
import Breadcrumbs from "../../controllers/breadCrumbs";
import { parentNavigate } from "../../controllers/ParentNavigate";
import {
  ButtonComponent,
  CommonTable,
  PageLimitDropDownComponent,
  PaginationComponent,
  SearchComponent,
  TableStatusDropdown,
} from "@shopeasy-dashboard-ui/ui-library";
import NoRecords from "../Main/NoRecords";
import Notification from "../Notification";
import AddReverseWithdrawalModal from "./AddReverseWithdrawalModal";
import useTableApi from "../../utilities/useTableApi";

const ReverseWithdrawalsList = () => {
  const handleTableApi = useTableApi();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total_collected: 0,
    remaining_balance: 0,
    total_transactions: 0,
    total_vendors: 0,
  });
  const { dataLoading, setDataLoading } = useContext(DataLoad) || {
    dataLoading: false,
    setDataLoading: () => { },
  };
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [limit, setLimit] = useState(localStorage.getItem("limit") || 10);

  const [showAddModal, setShowAddModal] = useState(false);

  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });

  // Sorting state & handler
  const [sortBy, setSortBy] = useState("createdAt");
  const [orderBy, setOrderBy] = useState({
    orderByValue: "DESC",
    isDecending: true,
  });

  const SortData = (sort_by, explicitOrder) => {
    setSortBy(sort_by);
    let nextOrder = "DESC";
    if (explicitOrder !== undefined) {
      nextOrder = (explicitOrder === "ASC" || explicitOrder === "A-Z" || explicitOrder === "1" || explicitOrder === true) ? "ASC" : "DESC";
    } else if (sortBy === sort_by) {
      const isCurrentlyAsc = (orderBy?.orderByValue === "ASC" || orderBy?.isDecending === false);
      nextOrder = isCurrentlyAsc ? "DESC" : "ASC";
    } else {
      nextOrder = "DESC";
    }
    setOrderBy({
      orderByValue: nextOrder,
      isDecending: nextOrder === "DESC",
    });
    setPage(1);
  };

  const handleNavigate = (path) => {
    parentNavigate(path);
    if (path.startsWith("/vendors") || path.startsWith("/reverse-withdrawals") || path.startsWith("/dashboard")) {
      navigate(path);
    }
  };

  const fetchReverseWithdrawals = async () => {
    setLoading(true);
    if (setDataLoading) setDataLoading(true);
    try {
      const res = await API({
        url: "/admin/vendor/reverse-withdrawals",
        method: "get",
        params: {
          page,
          limit,
          search: search?.trim() || undefined,
        },
      });
      if (res?.data?.data) {
        setRecords(res.data.data.records || []);
        setSummary(
          res.data.data.summary || {
            total_collected: 0,
            remaining_balance: 0,
            total_transactions: 0,
            total_vendors: 0,
          },
        );
        setTotalPages(res.data.data.totalPages || 1);
        setTotalRecords(res.data.data.total || 0);
      } else {
        setRecords([]);
      }
    } catch (err) {
      console.error("Error fetching reverse withdrawals:", err);
      setNotification({
        show: true,
        message:
          err?.response?.data?.message || err.message || "Failed to load reverse withdrawals",
        severity: "error",
      });
      setRecords([]);
    } finally {
      setLoading(false);
      if (setDataLoading) setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchReverseWithdrawals();
  }, [page, limit, search]);

  const columns = [
    {
      title: "Vendor",
      key: "vendor",
      headerClassName: "px-4 py-3 text-left w-full",
      cellClassName: "px-4 py-3 text-left w-full",
      render: (item) => {
        const vendorName = item.vendor?.shop_name || item.vendor?.name || item.vendor_name || item.shop_name || "Vendor";
        const email = item.vendor?.email || item.vendor_email || item.email;
        const logo = item.vendor?.profile_picture || item.vendor?.avatar || item.vendor?.vendor_logo || item.vendor?.logo || item.profile_picture || item.vendor_logo;

        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[var(--primary-bg-color)] primary-text-color flex items-center justify-center font-bold text-xs shrink-0 overflow-hidden border main-border-color">
              {logo ? (
                <img
                  src={logo}
                  alt={vendorName}
                  className="w-full h-full object-cover"
                />
              ) : (
                (vendorName ? vendorName[0] : "V").toUpperCase()
              )}
            </div>
            <div>
              <div className="font-semibold text-xs primary-text-color">
                {vendorName}
              </div>
              {email && (
                <div className="text-[11px] table-text mt-0.5">
                  {email}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Transaction Type",
      key: "transaction_type",
      headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[150px] min-w-[150px]",
      cellClassName: "px-4 py-3 text-left text-xs whitespace-nowrap w-[150px] min-w-[150px]",
      render: (item) => (
        <span className="px-2.5 py-1 rounded-md bg-[var(--primary-bg-color)] primary-text-color font-medium capitalize text-xs">
          {item.transaction_type}
        </span>
      ),
    },
    {
      title: "Balance Type",
      key: "balance_type",
      headerClassName: "px-4 py-3 text-center whitespace-nowrap w-[130px] min-w-[130px]",
      cellClassName: "px-4 py-3 text-center whitespace-nowrap w-[130px] min-w-[130px]",
      align: "text-center",
      render: (item) => (
        <span
          className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${item.balance_type === "credit"
            ? "bg-[var(--color-success-bg)] text-[var(--color-success)] border border-[var(--color-success)]"
            : "bg-[var(--color-danger-bg)] text-[var(--color-danger)] border border-[var(--color-danger)]"
            }`}
        >
          {item.balance_type}
        </span>
      ),
    },
    {
      title: "Amount",
      key: "amount",
      headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[130px] min-w-[130px]",
      cellClassName: "px-4 py-3 text-left text-xs whitespace-nowrap w-[130px] min-w-[130px]",
      render: (item) => (
        <span className="font-bold text-xs primary-text-color">
          ${Number(item.amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Collected",
      key: "total_collected",
      headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[130px] min-w-[130px]",
      cellClassName: "px-4 py-3 text-left text-xs whitespace-nowrap w-[130px] min-w-[130px]",
      render: (item) => (
        <span className="text-xs font-medium text-emerald-600">
          ${Number(item.total_collected || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Date",
      key: "createdAt",
      headerClassName: "px-4 py-3 text-left whitespace-nowrap w-[130px] min-w-[130px]",
      cellClassName: "px-4 py-3 text-left text-xs table-text whitespace-nowrap w-[130px] min-w-[130px]",
      render: (item) => (
        <span className="text-xs table-text">
          {new Date(item.createdAt || Date.now()).toLocaleDateString()}
        </span>
      ),
    },
    {
      title: "Notes",
      key: "notes",
      headerClassName: "px-4 py-3 text-left min-w-[150px]",
      cellClassName: "px-4 py-3 text-left text-xs table-text min-w-[150px]",
      render: (item) => (
        <span className="text-xs table-text max-w-xs truncate block">{item.notes || "-"}</span>
      ),
    },
  ];

  const tableHeaderFilter = (manageFieldsBtn) => (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b main-border-color bg-[var(--secondary-bg-color)] min-h-[52px]">
      <div className="flex items-center gap-2 shrink-0">
        <TableStatusDropdown
          value="all"
          onChange={() => { }}
          options={[
            { label: "All", value: "all", count: (totalRecords || 0).toString().padStart(2, "0") },
          ]}
        />
      </div>
      <div className="flex items-center gap-2.5 ml-auto shrink-0">
        <div className="hidden md:flex items-center gap-2.5">
          <div className="w-48 lg:w-64">
            <SearchComponent
              placeholder="Search vendor name..."
              value={search}
              onChange={(e) => {
                setSearch(e?.target ? e.target.value : (e || ""));
                setPage(1);
              }}
              onClear={() => {
                setSearch("");
                setPage(1);
              }}
              isClearable={true}
              size="sm"
            />
          </div>

          <div className="w-20">
            <PageLimitDropDownComponent
              limit={limit}
              setLimit={setLimit}
              setPage={setPage}
              totalRecords={totalRecords}
            />
          </div>

          {/* Column Customizer & Sort By Button */}
          {manageFieldsBtn}
        </div>
      </div>
    </div>
  );

  var startRec = (page - 1) * limit + 1;
  var endRec = Math.min(page * limit, totalRecords);

  return (
    <>
      {notification.show && (
        <Notification
          message={notification.message}
          close={() => setNotification((prev) => ({ ...prev, show: false }))}
          severity={notification.severity}
        />
      )}

      {showAddModal && (
        <AddReverseWithdrawalModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            setNotification({
              show: true,
              message: "Reverse withdrawal created successfully",
              severity: "success",
            });
            fetchReverseWithdrawals();
          }}
        />
      )}

      <div className="h-auto overflow-y-auto overflow-x-hidden pb-[64px] p-5">
        {/* Navigation & Header */}
        <div className="flex items-end justify-between page-header-pb">
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => {
                e.preventDefault();
                handleNavigate("/dashboard");
              }}
              className="back-arrow"
            >
              <ArrowLeft />
            </button>
            <div>
              <Breadcrumbs
                crumbs={[
                  { name: "Dashboard", path: "/dashboard" },
                  { name: "Reverse Withdrawals", path: "/reverse-withdrawals" },
                ]}
              />
              <h4 className="text-xl md:text-2xl font-semibold text-primary">
                Reverse Withdrawals
              </h4>
            </div>
          </div>

          <div className="flex space-x-3 items-center">
            <ButtonComponent
              variant="primary"
              size="md"
              icon={<PlusCircle className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />}
              onClick={() => setShowAddModal(true)}
            >
              Add Reverse Withdrawal
            </ButtonComponent>
          </div>
        </div>

        {/* Summary Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <div className="secondary-bg-color rounded-[8px] border main-border-color p-4 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-xs table-text font-medium">Total Collected</div>
              <div className="text-lg font-bold text-emerald-600 mt-1">
                ${Number(summary.total_collected || 0).toFixed(2)}
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <CashCoin size={20} />
            </div>
          </div>

          <div className="secondary-bg-color rounded-[8px] border main-border-color p-4 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-xs table-text font-medium">Remaining Balance</div>
              <div className="text-lg font-bold text-amber-600 mt-1">
                ${Number(summary.remaining_balance || 0).toFixed(2)}
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Wallet2 size={20} />
            </div>
          </div>

          <div className="secondary-bg-color rounded-[8px] border main-border-color p-4 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-xs table-text font-medium">Total Transactions</div>
              <div className="text-lg font-bold primary-text-color mt-1">
                {summary.total_transactions || 0}
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-[var(--primary-bg-color)] primary-text-color flex items-center justify-center">
              <ArrowRepeat size={20} />
            </div>
          </div>

          <div className="secondary-bg-color rounded-[8px] border main-border-color p-4 shadow-sm flex items-center justify-between">
            <div>
              <div className="text-xs table-text font-medium">Active Vendors</div>
              <div className="text-lg font-bold primary-text-color mt-1">
                {summary.total_vendors || 0}
              </div>
            </div>
            <div className="w-10 h-10 rounded-lg bg-[var(--primary-bg-color)] primary-text-color flex items-center justify-center">
              <People size={20} />
            </div>
          </div>
        </div>

        {/* Common Table Card Container */}
        <div className="flex w-full h-full overflow-hidden relative flex-col">
          <CommonTable
            tableId="reverse_withdrawals"
            tableHeader={tableHeaderFilter}
            tableFooter={
              totalRecords > 0 ? (
                <PaginationComponent
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  totalRecords={totalRecords}
                  startRecord={startRec}
                  endRecord={endRec}
                  limit={limit}
                  recordsLabel="Records"
                />
              ) : null
            }
            columns={columns}
            data={records}
            loading={loading || dataLoading}
            skeletonRows={10}
            onSort={SortData}
            sortBy={sortBy}
            orderBy={orderBy}
            NEWAPI={handleTableApi}
            tableDefaults={["vendor", "total_due", "total_collected", "createdAt", "notes"]}
            tableWiseAllowedSortKeys={["vendor", "total_due", "total_collected", "createdAt"]}
            emptyMessage={<NoRecords />}
          />
        </div>
      </div>
    </>
  );
};

export default ReverseWithdrawalsList;
